import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiBookOpen, FiCalendar, FiCreditCard, FiDollarSign, FiMail, FiSearch, FiShoppingBag, FiSliders, FiTag, FiUser } from 'react-icons/fi';
import api from '../../services/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 clamp(0.75rem, 2vw, 1.5rem) clamp(1.5rem, 4vw, 3rem);
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: clamp(1rem, 2vw, 1.5rem);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BackfillButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 38px;
  padding: 0 1rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--primary-color);
  color: var(--text-inverse);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StatusMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid ${props => props.error ? 'rgba(239, 68, 68, 0.25)' : 'rgba(34, 197, 94, 0.25)'};
  border-radius: 10px;
  background: ${props => props.error ? 'rgba(239, 68, 68, 0.08)' : 'rgba(34, 197, 94, 0.08)'};
  color: ${props => props.error ? 'var(--error-color)' : 'var(--success-color)'};
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: minmax(16rem, 1fr) minmax(9.5rem, 0.32fr) minmax(12rem, 0.42fr);
  gap: 8px;
  align-items: center;
  margin-bottom: 14px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;

  @media (max-width: 1024px) {
    grid-template-columns: minmax(14rem, 1fr) minmax(9.5rem, 0.45fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.625rem;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: clamp(1.75rem, 2vw + 1rem, 2.25rem);
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.025em;
  font-family: 'Inter', sans-serif;

  @media (max-width: 480px) {
    font-size: 1.625rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 36px;
  min-height: 36px;
  padding: 0 12px 0 36px;
  border: 0.5px solid #d6d6d6;
  border-radius: 8px;
  background: #fff;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  box-sizing: border-box;
  box-shadow: none;
  transition: var(--transition);

  @media (max-width: 480px) {
    font-size: 16px;
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.08);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.875rem;
  top: 0;
  height: 36px;
  display: flex;
  align-items: center;
  color: var(--text-muted);
  pointer-events: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SelectWrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 152px;

  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const FieldIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 0;
  height: 36px;
  display: flex;
  align-items: center;
  color: var(--color-text-secondary, var(--text-secondary));
  pointer-events: none;
  z-index: 1;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 36px;
  min-height: 36px;
  padding: 0 34px 0 34px;
  border: 0.5px solid #d6d6d6;
  border-radius: 8px;
  background: #fff;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 400;
  appearance: none;
  cursor: pointer;
  box-shadow: none;
  min-width: 0;
  box-sizing: border-box;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.08);
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 1rem;
  top: 50%;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #333;
  transform: translateY(-35%);
  pointer-events: none;
`;

const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.875rem, 2vw, 1.25rem);
`;

const Card = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.06));
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md, 0 10px 24px rgba(0, 0, 0, 0.08));
  }

  @media (hover: none) {
    &:hover {
      transform: none;
      box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.06));
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.25rem;
  padding: clamp(1rem, 2vw, 1.25rem) clamp(1rem, 2vw, 1.5rem);
  border-bottom: 1px solid var(--border-color);
  background: var(--background-secondary);

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const HeaderMain = styled.div`
  min-width: 0;
`;

const OrderNumber = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  min-width: 0;
  overflow-wrap: anywhere;

  svg {
    flex-shrink: 0;
  }
`;

const TypeLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-top: 0.375rem;
  font-family: 'Inter', sans-serif;
  overflow-wrap: anywhere;
`;

const HeaderAmount = styled.div`
  color: var(--success-color);
  font-size: 1.25rem;
  font-weight: 700;
  text-align: right;
  font-family: 'Inter', sans-serif;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;

  @media (max-width: 640px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.35rem 0.75rem;
  border: 1px solid rgba(34, 197, 94, 0.2);
  color: var(--success-color);
  background: rgba(34, 197, 94, 0.1);
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 999px;
  text-transform: capitalize;
  font-family: 'Inter', sans-serif;
`;

const RefundBadge = styled(Badge)`
  border-color: rgba(245, 158, 11, 0.25);
  color: #b45309;
  background: rgba(245, 158, 11, 0.1);
`;

const CardBody = styled.div`
  padding: clamp(1rem, 2vw, 1.5rem);
`;

const ItemTitle = styled.div`
  color: var(--text-primary);
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  font-family: 'Inter', sans-serif;
  overflow-wrap: anywhere;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  overflow-wrap: anywhere;
`;

const ItemCode = styled.span`
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 500;
`;

const FilterContext = styled.div`
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-family: 'Inter', sans-serif;
  margin: -0.75rem 0 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr));
  gap: 1rem 1.5rem;
`;

const Field = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-family: 'Inter', sans-serif;
  min-width: 0;

  svg {
    color: var(--text-muted);
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const Value = styled.div`
  color: var(--text-primary);
  font-weight: 600;
  margin-top: 0.25rem;
  overflow-wrap: anywhere;
  text-transform: none;
  letter-spacing: normal;
  font-size: 0.9rem;
  line-height: 1.45;
  word-break: break-word;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: clamp(2.5rem, 8vw, 4rem) clamp(1rem, 4vw, 2rem);
  color: var(--text-secondary);
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.06));

  svg {
    color: var(--text-muted);
    margin-bottom: 0.75rem;
  }

  p {
    margin: 0.25rem 0 0;
    font-family: 'Inter', sans-serif;
    line-height: 1.5;
  }
`;

const formatAmount = amount => `$${Number(amount || 0).toFixed(2)}`;

const formatDate = date => date ? new Date(date).toLocaleString() : 'N/A';

const formatStatus = status => status === 'succeeded' ? 'completed' : status;

const formatRefundStatus = status => status ? `${status} refund` : 'Refund issued';

function PurchaseSearch() {
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState('');
  const [backfillError, setBackfillError] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.searchAdminPurchases(search.trim());
        setResults(response.purchases || []);
      } catch (err) {
        setError('Failed to search purchases. Please try again.');
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [search]);

  const handleBackfill = async () => {
    try {
      setBackfilling(true);
      setBackfillMessage('Backfilling payment metadata from Stripe...');
      setBackfillError(false);

      const response = await api.backfillPaymentMetadata(200);
      const courseResult = response.results?.coursePayments || { updated: 0, failed: 0 };
      const productResult = response.results?.productPurchases || { updated: 0, failed: 0 };
      const updated = courseResult.updated + productResult.updated;
      const failed = courseResult.failed + productResult.failed;

      setBackfillMessage(`Backfill complete. Updated ${updated} records${failed ? `, ${failed} failed` : ''}.`);
      setBackfillError(failed > 0);

      const refreshed = await api.searchAdminPurchases(search.trim());
      setResults(refreshed.purchases || []);
    } catch (err) {
      const backendError = err?.data?.error || err?.message;
      setBackfillMessage(backendError
        ? `Backfill failed: ${backendError}`
        : 'Backfill failed. Confirm production is using the correct Stripe secret key and try again.');
      setBackfillError(true);
    } finally {
      setBackfilling(false);
    }
  };

  const courseOptions = Array.from(new Set(
    results
      .filter(result => result.type === 'course')
      .flatMap(result => (result.items && result.items.length > 0 ? result.items : [{ name: result.itemName }]))
      .map(item => item.name)
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  const visibleResults = results
    .map(result => {
      if (!courseFilter || result.type !== 'course') return result;

      const matchingItems = (result.items || [])
        .filter(item => item.name === courseFilter);

      if (matchingItems.length === 0) return null;

      return {
        ...result,
        itemName: matchingItems.map(item => item.name).join(', '),
        itemCode: matchingItems.map(item => item.code).filter(Boolean).join(', '),
        items: matchingItems,
        filteredCourseName: courseFilter
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (sortBy === 'date-asc') return new Date(a.purchaseDate) - new Date(b.purchaseDate);
      if (sortBy === 'name-asc') return a.itemName.localeCompare(b.itemName);
      if (sortBy === 'name-desc') return b.itemName.localeCompare(a.itemName);
      return new Date(b.purchaseDate) - new Date(a.purchaseDate);
    });

  return (
    <Container>
      <Header>
        <Title>Purchase Lookup</Title>
        <BackfillButton type="button" onClick={handleBackfill} disabled={backfilling}>
          <FiCreditCard />
          {backfilling ? 'Backfilling...' : 'Backfill Payment Data'}
        </BackfillButton>
      </Header>

      <Controls>
        <SearchBar>
          <SearchIcon><FiSearch /></SearchIcon>
          <SearchInput
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search name, email, order #, last 4, Stripe ID, or date"
          />
        </SearchBar>
        <SelectWrap>
          <FieldIcon><FiSliders /></FieldIcon>
          <Select value={sortBy} onChange={event => setSortBy(event.target.value)} aria-label="Sort purchases">
            <option value="date-desc">Newest first</option>
            <option value="date-asc">Oldest first</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
          </Select>
          <SelectArrow />
        </SelectWrap>
        <SelectWrap>
          <FieldIcon><FiBookOpen /></FieldIcon>
          <Select value={courseFilter} onChange={event => setCourseFilter(event.target.value)} aria-label="Filter by course name">
            <option value="">All Courses</option>
            {courseOptions.map(courseName => (
              <option key={courseName} value={courseName}>{courseName}</option>
            ))}
          </Select>
          <SelectArrow />
        </SelectWrap>
      </Controls>

      {backfillMessage && <StatusMessage error={backfillError}>{backfillMessage}</StatusMessage>}

      {error && <EmptyState>{error}</EmptyState>}
      {!error && loading && <EmptyState>Searching purchases...</EmptyState>}
      {!error && !loading && visibleResults.length === 0 && (
        <EmptyState>
          <FiShoppingBag size={36} />
          <p>No purchases found.</p>
          <p>Try searching by name, email, order number, or date.</p>
        </EmptyState>
      )}

      {!error && !loading && visibleResults.length > 0 && (
        <Results>
          {visibleResults.map(result => (
            <Card key={`${result.type}-${result.id}`}>
              <CardHeader>
                <HeaderMain>
                  <OrderNumber>
                    {result.type === 'course' ? <FiBookOpen /> : <FiShoppingBag />}
                    {result.orderNumber}
                  </OrderNumber>
                  <TypeLabel>{result.type === 'course' ? 'Course Enrollment' : 'Product Purchase'}</TypeLabel>
                </HeaderMain>
                <HeaderActions>
                  <HeaderAmount>{formatAmount(result.amount)}</HeaderAmount>
                  <Badge>{formatStatus(result.status)}</Badge>
                  {result.refundAmount > 0 && <RefundBadge>{formatRefundStatus(result.refundStatus)}</RefundBadge>}
                </HeaderActions>
              </CardHeader>
              <CardBody>
                {result.items && result.items.length > 1 ? (
                  <ItemList>
                    {result.items.map((item, index) => (
                      <ItemRow key={`${result.id}-${item.code || item.name}-${index}`}>
                        <FiBookOpen />
                        <span>{item.name}</span>
                        {item.code && <ItemCode>({item.code})</ItemCode>}
                      </ItemRow>
                    ))}
                  </ItemList>
                ) : (
                  <ItemTitle>{result.itemName}</ItemTitle>
                )}
                {result.filteredCourseName && (
                  <FilterContext>Matched course from order {result.orderNumber}</FilterContext>
                )}
                <Grid>
                  <Field><FiUser /> <div>Customer<Value>{result.firstName} {result.lastName}</Value></div></Field>
                  <Field><FiMail /> <div>Email<Value>{result.email}</Value></div></Field>
                  <Field><FiCalendar /> <div>Date<Value>{formatDate(result.purchaseDate)}</Value></div></Field>
                  <Field><FiBookOpen /> <div>Item Code<Value>{result.itemCode || 'N/A'}</Value></div></Field>
                  <Field><FiTag /> <div>Promo Code<Value>{result.promoCode || 'None'}</Value></div></Field>
                  <Field><FiDollarSign /> <div>Discount Amount<Value>{result.discountAmount ? formatAmount(result.discountAmount) : 'N/A'}</Value></div></Field>
                  <Field><FiDollarSign /> <div>Refund Amount<Value>{result.refundAmount > 0 ? formatAmount(result.refundAmount) : 'None'}</Value></div></Field>
                  <Field><FiCalendar /> <div>Refund Date<Value>{result.refundedAt ? formatDate(result.refundedAt) : 'N/A'}</Value></div></Field>
                  <Field><FiCreditCard /> <div>Method Type<Value>{result.paymentMethodType || 'N/A'}</Value></div></Field>
                  <Field><FiCreditCard /> <div>Brand<Value>{result.paymentMethodBrand || 'N/A'}</Value></div></Field>
                  <Field><FiCreditCard /> <div>Last 4<Value>{result.paymentMethodLast4 || 'N/A'}</Value></div></Field>
                  <Field><FiCreditCard /> <div>Wallet Type<Value>{result.walletType || 'N/A'}</Value></div></Field>
                  <Field><FiDollarSign /> <div>Amount<Value>{formatAmount(result.amount)}</Value></div></Field>
                  <Field><FiDollarSign /> <div>Net Amount<Value>{result.refundAmount > 0 ? formatAmount(result.amount - result.refundAmount) : formatAmount(result.amount)}</Value></div></Field>
                  <Field><FiCreditCard /> <div>Stripe Payment ID<Value>{result.stripePaymentIntentId}</Value></div></Field>
                </Grid>
              </CardBody>
            </Card>
          ))}
        </Results>
      )}
    </Container>
  );
}

export default PurchaseSearch;
